import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Settings, 
  Activity, 
  Zap, 
  ChevronDown, 
  ChevronUp,
  Monitor,
  Smartphone,
  Package
} from 'lucide-react';
import { usePerformanceBudget } from '@/hooks/usePerformanceBudget';
import { useConnectionMode } from '@/connection/SimpleConnectionProvider';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BundleVerification } from './BundleVerification';
import { SkeletonShowcase } from './SkeletonShowcase';

export const DevPerformancePanel = () => {
  const [isOpen, setIsOpen] = useState(false);
  const connectionMode = useConnectionMode();
  const { 
    performanceScore, 
    violations, 
    warnings, 
    overallStatus,
    budgetType 
  } = usePerformanceBudget();

  // Only show in development or when explicitly enabled
  const shouldShow = import.meta.env.DEV || 
                    localStorage.getItem('showPerformancePanel') === 'true';

  if (!shouldShow) return null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'good': return 'bg-green-500';
      case 'warning': return 'bg-yellow-500';
      case 'critical': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="w-full">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        {/* Collapsed State - Mini Indicator */}
        {!isOpen && (
          <CollapsibleTrigger asChild>
            <Button
              size="sm"
              variant="outline"
              className="bg-white shadow-lg border-2 hover:shadow-xl transition-all"
            >
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${getStatusColor(overallStatus)}`} />
                <Zap className="h-4 w-4" />
                <span className="font-mono text-xs">{performanceScore}</span>
                <Badge variant="secondary" className="text-xs px-1">
                  {budgetType.toUpperCase()}
                </Badge>
                <ChevronUp className="h-3 w-3" />
              </div>
            </Button>
          </CollapsibleTrigger>
        )}

        {/* Expanded State - Full Panel with Tabs */}
        <CollapsibleContent asChild>
          <Card className="bg-white shadow-xl border-2 w-full">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <Zap className="h-4 w-4" />
                  Dev Performance Monitor
                </div>
                <CollapsibleTrigger asChild>
                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0">
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </CollapsibleTrigger>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <Tabs defaultValue="metrics" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="metrics">Metrics</TabsTrigger>
                  <TabsTrigger value="bundle">Bundle</TabsTrigger>
                  <TabsTrigger value="skeleton">Skeletons</TabsTrigger>
                </TabsList>
                
                {/* Metrics Tab */}
                <TabsContent value="metrics" className="space-y-3">
                  {/* Overall Score */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium">Performance Score</span>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${getStatusColor(overallStatus)}`} />
                        <span className="font-mono text-sm">{performanceScore}/100</span>
                      </div>
                    </div>
                    <Progress value={performanceScore} className="h-1" />
                  </div>

                  {/* Connection Mode */}
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1">
                      {connectionMode === 'lite' ? (
                        <Smartphone className="h-3 w-3" />
                      ) : (
                        <Monitor className="h-3 w-3" />
                      )}
                      <span>Connection:</span>
                    </div>
                    <Badge variant={connectionMode === 'lite' ? 'secondary' : 'default'} className="text-xs">
                      {connectionMode.toUpperCase()}
                    </Badge>
                  </div>

                  {/* Budget Status */}
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="text-center p-2 bg-red-50 rounded">
                      <div className="font-bold text-red-700">{violations.length}</div>
                      <div className="text-red-600">Violations</div>
                    </div>
                    <div className="text-center p-2 bg-yellow-50 rounded">
                      <div className="font-bold text-yellow-700">{warnings.length}</div>
                      <div className="text-yellow-600">Warnings</div>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="flex gap-1">
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="flex-1 h-7 text-xs"
                      onClick={() => window.location.reload()}
                    >
                      <Activity className="h-3 w-3 mr-1" />
                      Refresh
                    </Button>
                    <Button
                      size="sm"
                      variant="outline" 
                      className="h-7 px-2"
                      onClick={() => console.log('Performance data:', { performanceScore, violations, warnings, budgetType })}
                    >
                      <Settings className="h-3 w-3" />
                    </Button>
                  </div>

                  {/* Budget Type Indicator */}
                  <div className="text-center pt-1 border-t">
                    <span className="text-xs text-gray-500">
                      Budget: {budgetType === 'lite' ? 'Lite (≤80KB JS)' : 'Full (≤250KB JS)'}
                    </span>
                  </div>
                </TabsContent>
                
                {/* Bundle Verification Tab */}
                <TabsContent value="bundle">
                  <BundleVerification />
                </TabsContent>
                
                {/* Skeleton Showcase Tab */}
                <TabsContent value="skeleton">
                  <SkeletonShowcase />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};